import { apiService } from "./apiService";
import { API_ENDPOINTS, getEndpoint, getQueryEndpoint } from "./apiEndpoints";

export const accountService = {
  login: async (credentials) => {
    return await apiService.post(API_ENDPOINTS.ACCOUNTS.LOGIN, credentials);
  },
  logout: async (data) => {
    return await apiService.post(API_ENDPOINTS.ACCOUNTS.LOGOUT, data);
  },
  signup: async (userData) => {
    return await apiService.post(API_ENDPOINTS.ACCOUNTS.SIGNUP, userData);
  },
  getProfile: async () => {
    return await apiService.get(API_ENDPOINTS.ACCOUNTS.PROFILE);
  },
  updateProfile: async (profileData) => {
    return await apiService.put(API_ENDPOINTS.ACCOUNTS.PROFILE, profileData);
  },
  patchProfile: async (profileData) => {
    return await apiService.patch(API_ENDPOINTS.ACCOUNTS.PROFILE, profileData);
  },
  deleteProfile: async () => {
    return await apiService.delete(API_ENDPOINTS.ACCOUNTS.PROFILE);
  },
  changePin: async (pinData) => {
    return await apiService.post(API_ENDPOINTS.ACCOUNTS.CHANGE_PIN, pinData);
  },
  forgotPin: async (forgotData) => {
    return await apiService.post(API_ENDPOINTS.ACCOUNTS.FORGOT_PIN, forgotData);
  },
  requestOtp: async (requestData) => {
    return await apiService.post(
      API_ENDPOINTS.ACCOUNTS.REQUEST_OTP,
      requestData
    );
  },
  verifyOtp: async (verifyData) => {
    return await apiService.post(API_ENDPOINTS.ACCOUNTS.VERIFY_OTP, verifyData);
  },
  refreshToken: async (refreshData) => {
    return await apiService.post(
      API_ENDPOINTS.ACCOUNTS.TOKEN_REFRESH,
      refreshData
    );
  },
  testProtected: async () => {
    return await apiService.get(API_ENDPOINTS.ACCOUNTS.PROTECTED);
  },
  requestInvoice: async () => {
    return await apiService.post(API_ENDPOINTS.ACCOUNTS.INVOICE_REQUEST);
  },
  getInvoiceList: async () => {
    return await apiService.get(API_ENDPOINTS.ACCOUNTS.INVOICE_GET);
  },
  getUserSubscriptionCount: async () => {
    return await apiService.get(API_ENDPOINTS.ACCOUNTS.USER_SUBSCRIPTION_COUNT);
  },
  getStates: async () => {
    try {
      // Try the main endpoint first
      return await apiService.get(API_ENDPOINTS.ACCOUNTS.STATES);
    } catch (error) {
      console.log("Primary states endpoint failed, trying alternatives");
      try {
        // Try alternative endpoint variations
        return await apiService.get("accounts/state/");
      } catch (error2) {
        try {
          return await apiService.get("accounts/countries/states/");
        } catch (error3) {
          try {
            return await apiService.get("locations/states/");
          } catch (error4) {
            throw error; // Throw the original error
          }
        }
      }
    }
  },
  getDistricts: async (stateId) => {
    try {
      // Try the main endpoint first
      return await apiService.get(
        getEndpoint(API_ENDPOINTS.ACCOUNTS.DISTRICTS, stateId)
      );
    } catch (error) {
      console.log("Primary districts endpoint failed, trying alternatives");
      try {
        // Try alternative endpoint variations
        return await apiService.get(`accounts/districts/districts/${stateId}/`);
      } catch (error2) {
        try {
          return await apiService.get(`accounts/states/${stateId}/districts/`);
        } catch (error3) {
          try {
            return await apiService.get(
              `locations/states/${stateId}/districts/`
            );
          } catch (error4) {
            throw error; // Throw the original error
          }
        }
      }
    }
  },
  getAllDistricts: async () => {
    try {
      // First get all states
      const statesResponse = await apiService.get(
        API_ENDPOINTS.ACCOUNTS.STATES
      );
      if (!statesResponse || !Array.isArray(statesResponse)) {
        throw new Error("No states found");
      }

      // Fetch districts for each state
      const allDistricts = [];
      for (const state of statesResponse) {
        try {
          const districtsResponse = await apiService.get(
            getEndpoint(API_ENDPOINTS.ACCOUNTS.DISTRICTS, state.id)
          );

          if (districtsResponse && Array.isArray(districtsResponse)) {
            allDistricts.push(...districtsResponse);
          } else if (
            districtsResponse &&
            districtsResponse.districts &&
            Array.isArray(districtsResponse.districts)
          ) {
            allDistricts.push(...districtsResponse.districts);
          }
        } catch (districtError) {
          console.warn(
            `Failed to fetch districts for state ${state.id}:`,
            districtError
          );
          // Continue with other states even if one fails
        }
      }

      return allDistricts;
    } catch (error) {
      console.error("Error fetching all districts:", error);
      throw error;
    }
  },
};

export const buyerService = {
  signup: async (buyerData) => {
    return await apiService.post(API_ENDPOINTS.BUYERS.SIGNUP, buyerData);
  },
  getProfile: async () => {
    return await apiService.get(API_ENDPOINTS.BUYERS.PROFILE);
  },
  getProfileById: async (buyerId) => {
    return await apiService.get(
      getEndpoint(API_ENDPOINTS.BUYERS.PROFILE_BY_ID, buyerId)
    );
  },
  updateProfile: async (profileData) => {
    return await apiService.patch(
      API_ENDPOINTS.BUYERS.PROFILE_UPDATE,
      profileData
    );
  },
  deleteProfile: async () => {
    return await apiService.delete(API_ENDPOINTS.BUYERS.PROFILE_DELETE);
  },
  changeProfileImage: async (imageData) => {
    return await apiService.patch(
      API_ENDPOINTS.BUYERS.PROFILE_CHANGE_IMAGE,
      imageData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
  },
  getInquiryList: async (params = {}) => {
    return await apiService.get(API_ENDPOINTS.BUYERS.INQUIRY_LIST, { params });
  },
  submitInquiry: async (inquiryData) => {
    return await apiService.post(
      API_ENDPOINTS.BUYERS.INQUIRY_SUBMIT,
      inquiryData
    );
  },
  getInterests: async () => {
    return await apiService.get(API_ENDPOINTS.BUYERS.INTERESTS_GET);
  },
  getInterestOptions: async () => {
    return await apiService.get(API_ENDPOINTS.BUYERS.INTERESTS_OPTIONS);
  },
  submitInterests: async (interestsData) => {
    return await apiService.post(
      API_ENDPOINTS.BUYERS.INTERESTS_SUBMIT,
      interestsData
    );
  },
  clickSanitarywareInquiry: async (productId, clickData) => {
    return await apiService.post(
      getEndpoint(API_ENDPOINTS.BUYERS.SANITARYWARE_INQUIRY_CLICK, productId),
      clickData
    );
  },
  clickTileInquiry: async (productId, clickData) => {
    return await apiService.post(
      getEndpoint(API_ENDPOINTS.BUYERS.TILE_INQUIRY_CLICK, productId),
      clickData
    );
  },
  getSanitarywareInquiryClicks: async (params = {}) => {
    return await apiService.get(
      API_ENDPOINTS.BUYERS.SANITARYWARE_INQUIRY_CLICK_LIST,
      { params }
    );
  },
  getTileInquiryClicks: async (params = {}) => {
    return await apiService.get(API_ENDPOINTS.BUYERS.TILE_INQUIRY_CLICK_LIST, {
      params,
    });
  },
};

export const sellerService = {
  signup: async (formData, config = {}) => {
    try {
      const response = await apiService.post(
        API_ENDPOINTS.SELLERS.SIGNUP,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            ...config.headers,
          },
        }
      );
      return response;
    } catch (error) {
      throw error;
    }
  },
  getProfile: async () => {
    return await apiService.get(API_ENDPOINTS.SELLERS.PROFILE);
  },
  getProfileById: async (sellerId) => {
    return await apiService.get(
      getEndpoint(API_ENDPOINTS.SELLERS.PROFILE_BY_ID, sellerId)
    );
  },
  updateProfile: async (profileData, sellerId) => {
    // Check if profileData is FormData
    const config =
      profileData instanceof FormData
        ? {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        : {};

    return await apiService.patch(
      API_ENDPOINTS.SELLERS.PROFILE_UPDATE(sellerId),
      profileData,
      config
    );
  },
  deleteProfile: async () => {
    return await apiService.delete(API_ENDPOINTS.SELLERS.PROFILE_DELETE);
  },
  changeProfileImage: async (imageData) => {
    return await apiService.patch(
      API_ENDPOINTS.SELLERS.PROFILE_CHANGE_IMAGE,
      imageData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
  },
  updateActiveStatus: async (sellerId, statusData) => {
    return await apiService.patch(
      getEndpoint(API_ENDPOINTS.SELLERS.PROFILE_ACTIVE_STATUS, sellerId),
      statusData
    );
  },
  getBuyerList: async (params = {}) => {
    return await apiService.get(API_ENDPOINTS.SELLERS.BUYER_LIST, { params });
  },
  getCatalogs: async (params = {}) => {
    return await apiService.get(API_ENDPOINTS.SELLERS.CATALOGS, { params });
  },
  getCatalogsBySeller: async (sellerId, params = {}) => {
    return await apiService.get(
      getEndpoint(API_ENDPOINTS.SELLERS.CATALOGS_BY_SELLER, sellerId),
      { params }
    );
  },
  uploadCatalog: async (catalogData) => {
    return await apiService.upload(
      API_ENDPOINTS.SELLERS.CATALOGS_UPLOAD,
      catalogData
    );
  },
  uploadCatalogChunk: async (chunkData) => {
    return await apiService.upload(
      API_ENDPOINTS.SELLERS.CATALOGS_CHUNK_UPLOAD,
      chunkData
    );
  },
  deleteCatalog: async (catalogId) => {
    return await apiService.delete(
      getEndpoint(API_ENDPOINTS.SELLERS.CATALOGS_DELETE, catalogId)
    );
  },
  getCatalogsUsedStorage: async (sellerId = null) => {
    const endpoint = sellerId
      ? getEndpoint(API_ENDPOINTS.SELLERS.CATALOGS_USED_STORAGE, sellerId)
      : API_ENDPOINTS.SELLERS.CATALOGS_USED_STORAGE;
    return await apiService.get(endpoint);
  },
  getCategoryList: async (params = {}) => {
    return await apiService.get(API_ENDPOINTS.SELLERS.CATEGORY_LIST, {
      params,
    });
  },
  getSellerReviews: async (sellerId, params = {}) => {
    return await apiService.get(
      getEndpoint(
        API_ENDPOINTS.SELLERS.SOCIAL_SELLER_REVIEW_BY_SELLER,
        sellerId
      ),
      { params }
    );
  },
  deleteReview: async (reviewId) => {
    return await apiService.delete(
      getEndpoint(API_ENDPOINTS.SELLERS.SOCIAL_SELLER_REVIEW_DELETE, reviewId)
    );
  },
  getPosts: async (params = {}) => {
    return await apiService.get(API_ENDPOINTS.SOCIAL_POSTS.LIST, { params });
  },
  createPost: async (postData) => {
    return await apiService.upload(API_ENDPOINTS.SOCIAL_POSTS.CREATE, postData);
  },
  getPostDetail: async (postId) => {
    return await apiService.get(
      getEndpoint(API_ENDPOINTS.SOCIAL_POSTS.DETAIL, postId)
    );
  },
  updatePost: async (postId, postData) => {
    return await apiService.patch(
      getEndpoint(API_ENDPOINTS.SOCIAL_POSTS.UPDATE, postId),
      postData
    );
  },
  deletePost: async (postId) => {
    return await apiService.delete(
      getEndpoint(API_ENDPOINTS.SOCIAL_POSTS.DELETE, postId)
    );
  },
  getTileInquiryClickList: async (params = {}) => {
    return await apiService.get(API_ENDPOINTS.SELLERS.TILE_INQUIRY_CLICK_LIST, {
      params,
    });
  },
  getSanitarywareInquiryClickList: async (params = {}) => {
    return await apiService.get(
      API_ENDPOINTS.SELLERS.SANITARYWARE_INQUIRY_CLICK_LIST,
      {
        params,
      }
    );
  },
};

export const sanitarywareService = {
  getList: async (params = {}) => {
    return await apiService.post(API_ENDPOINTS.SANITARYWARE.LIST, params);
  },
  create: async (productData) => {
    return await apiService.post(
      API_ENDPOINTS.SANITARYWARE.CREATE,
      productData
    );
  },
  getDetail: async (id) => {
    return await apiService.get(
      getEndpoint(API_ENDPOINTS.SANITARYWARE.DETAIL, id)
    );
  },
  update: async (id, productData) => {
    return await apiService.patch(
      getEndpoint(API_ENDPOINTS.SANITARYWARE.UPDATE, id),
      productData
    );
  },
  delete: async (id) => {
    return await apiService.delete(
      getEndpoint(API_ENDPOINTS.SANITARYWARE.DELETE, id)
    );
  },
  updateActivationStatus: async (productId, statusData) => {
    return await apiService.post(
      getEndpoint(API_ENDPOINTS.SANITARYWARE.ACTIVATION_STATUS, productId),
      statusData
    );
  },
  updatePublishStatus: async (productId, statusData) => {
    let requestData;
    if (typeof statusData === "string") {
      requestData = { action: statusData };
    } else if (typeof statusData === "object" && statusData.action) {
      requestData = statusData;
    } else {
      requestData = { action: statusData };
    }
    const url = getQueryEndpoint(
      getEndpoint(API_ENDPOINTS.SANITARYWARE.PUBLISH_STATUS, productId),
      requestData
    );
    return await apiService.post(url);
  },
  getImages: async (productId) => {
    return await apiService.get(
      getEndpoint(API_ENDPOINTS.SANITARYWARE.IMAGES_LIST, productId)
    );
  },
  uploadImages: async (productId, imageData) => {
    return await apiService.upload(
      getEndpoint(API_ENDPOINTS.SANITARYWARE.IMAGES_UPLOAD, productId),
      imageData
    );
  },
  getImageDetail: async (productId, imageId) => {
    return await apiService.get(
      getEndpoint(API_ENDPOINTS.SANITARYWARE.IMAGES_DETAIL, productId, imageId)
    );
  },
  deleteImage: async (productId, imageId) => {
    return await apiService.delete(
      getEndpoint(API_ENDPOINTS.SANITARYWARE.IMAGES_DELETE, productId, imageId)
    );
  },
  changePrimaryImage: async (productId, imageId) => {
    return await apiService.patch(
      getEndpoint(
        API_ENDPOINTS.SANITARYWARE.IMAGES_CHANGE_PRIMARY,
        productId,
        imageId
      )
    );
  },
  getAttributes: async (params) => {
    if (params && params.type) {
      return await apiService.get(
        getEndpoint(API_ENDPOINTS.SANITARYWARE.TYPES_FILTERS, params.type)
      );
    } else {
      return await apiService.get(API_ENDPOINTS.SANITARYWARE.ATTRIBUTES_LIST, {
        params,
      });
    }
  },
  createAttribute: async (attributeData) => {
    return await apiService.post(
      API_ENDPOINTS.SANITARYWARE.ATTRIBUTES_CREATE,
      attributeData
    );
  },
  getAttributeDetail: async (attrId) => {
    return await apiService.get(
      getEndpoint(API_ENDPOINTS.SANITARYWARE.ATTRIBUTES_DETAIL, attrId)
    );
  },
  updateAttribute: async (attrId, attributeData) => {
    return await apiService.post(
      getEndpoint(API_ENDPOINTS.SANITARYWARE.ATTRIBUTES_UPDATE, attrId),
      attributeData
    );
  },
  deleteAttribute: async (attrId) => {
    return await apiService.post(
      getEndpoint(API_ENDPOINTS.SANITARYWARE.ATTRIBUTES_DELETE, attrId)
    );
  },
  previewUpdateAttribute: async (attrId, attributeData) => {
    return await apiService.post(
      getEndpoint(API_ENDPOINTS.SANITARYWARE.ATTRIBUTES_PREVIEW_UPDATE, attrId),
      attributeData
    );
  },
  undoDeprecationAttribute: async (attrId) => {
    return await apiService.post(
      getEndpoint(
        API_ENDPOINTS.SANITARYWARE.ATTRIBUTES_UNDO_DEPRECATION,
        attrId
      )
    );
  },
  getTypes: async (params = {}) => {
    return await apiService.get(API_ENDPOINTS.SANITARYWARE.TYPES_LIST, {
      params,
    });
  },
  createType: async (typeData) => {
    return await apiService.post(
      API_ENDPOINTS.SANITARYWARE.TYPES_CREATE,
      typeData
    );
  },
  updateType: async (typeId, typeData) => {
    return await apiService.put(
      getEndpoint(API_ENDPOINTS.SANITARYWARE.TYPES_UPDATE, typeId),
      typeData
    );
  },
  deleteType: async (typeId) => {
    return await apiService.delete(
      getEndpoint(API_ENDPOINTS.SANITARYWARE.TYPES_DELETE, typeId)
    );
  },
  undoDeprecationType: async (typeId) => {
    return await apiService.post(
      getEndpoint(API_ENDPOINTS.SANITARYWARE.TYPES_UNDO_DEPRECATION, typeId)
    );
  },
  assignAttribute: async (typeId, attributeData) => {
    return await apiService.post(
      getEndpoint(API_ENDPOINTS.SANITARYWARE.TYPES_ASSIGN_ATTRIBUTE, typeId),
      attributeData
    );
  },
  removeAttribute: async (typeId, attributeData) => {
    return await apiService.post(
      getEndpoint(API_ENDPOINTS.SANITARYWARE.TYPES_REMOVE_ATTRIBUTE, typeId),
      attributeData
    );
  },
  getTypeFilters: async (typeId) => {
    return await apiService.get(
      getEndpoint(API_ENDPOINTS.SANITARYWARE.TYPES_FILTERS, typeId)
    );
  },

  // PDF methods
  getPdfs: async (productId) => {
    return await apiService.get(
      getEndpoint(API_ENDPOINTS.SANITARYWARE.PDFS_LIST, productId)
    );
  },

  uploadPdf: async (productId, pdfData) => {
    return await apiService.upload(
      getEndpoint(API_ENDPOINTS.SANITARYWARE.PDFS_UPLOAD, productId),
      pdfData
    );
  },

  deletePdf: async (productId, pdfId) => {
    return await apiService.delete(
      getEndpoint(API_ENDPOINTS.SANITARYWARE.PDFS_DELETE, productId, pdfId)
    );
  },

  getPdfDetail: async (productId, pdfId) => {
    return await apiService.get(
      getEndpoint(API_ENDPOINTS.SANITARYWARE.PDFS_DETAIL, productId, pdfId)
    );
  },

  replacePdf: async (productId, pdfId, pdfData) => {
    // Since the replace endpoint is not working, implement as delete + upload
    try {
      // First delete the existing PDF
      await apiService.delete(
        getEndpoint(API_ENDPOINTS.SANITARYWARE.PDFS_DELETE, productId, pdfId)
      );
      // Then upload the new PDF
      return await apiService.upload(
        getEndpoint(API_ENDPOINTS.SANITARYWARE.PDFS_UPLOAD, productId),
        pdfData
      );
    } catch (error) {
      console.error("Error replacing PDF:", error);
      throw error;
    }
  },
};

export const tileService = {
  getList: async (params = {}) => {
    return await apiService.post(API_ENDPOINTS.TILES.LIST, params);
  },

  create: async (productData) => {
    return await apiService.post(API_ENDPOINTS.TILES.CREATE, productData);
  },

  getDetail: async (id) => {
    return await apiService.get(getEndpoint(API_ENDPOINTS.TILES.DETAIL, id));
  },

  update: async (id, productData) => {
    return await apiService.patch(
      getEndpoint(API_ENDPOINTS.TILES.UPDATE, id),
      productData
    );
  },

  delete: async (id) => {
    return await apiService.delete(getEndpoint(API_ENDPOINTS.TILES.DELETE, id));
  },

  updateActivationStatus: async (productId, statusData) => {
    return await apiService.post(
      getEndpoint(API_ENDPOINTS.TILES.ACTIVATION_STATUS, productId),
      statusData
    );
  },

  updatePublishStatus: async (productId, statusData) => {
    let requestData;
    if (typeof statusData === "string") {
      requestData = { action: statusData };
    } else if (typeof statusData === "object" && statusData.action) {
      requestData = statusData;
    } else {
      requestData = { action: statusData };
    }
    const url = getQueryEndpoint(
      getEndpoint(API_ENDPOINTS.TILES.PUBLISH_STATUS, productId),
      requestData
    );
    return await apiService.post(url);
  },

  getPdfs: async (productId) => {
    return await apiService.get(
      getEndpoint(API_ENDPOINTS.TILES.PDFS_LIST, productId)
    );
  },

  uploadPdf: async (productId, pdfData) => {
    return await apiService.upload(
      getEndpoint(API_ENDPOINTS.TILES.PDFS_UPLOAD, productId),
      pdfData
    );
  },

  deletePdf: async (productId, pdfId) => {
    return await apiService.delete(
      getEndpoint(API_ENDPOINTS.TILES.PDFS_DELETE, productId, pdfId)
    );
  },

  getPdfDetail: async (productId, pdfId) => {
    return await apiService.get(
      getEndpoint(API_ENDPOINTS.TILES.PDFS_DETAIL, productId, pdfId)
    );
  },

  replacePdf: async (productId, pdfId, pdfData) => {
    try {
      // Try PATCH method for the replace endpoint
      const endpoint = getEndpoint(API_ENDPOINTS.TILES.PDFS_REPLACE, productId, pdfId);
      
      const result = await apiService.patch(endpoint, pdfData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      
      return result;
    } catch (error) {
      console.warn("PATCH method failed, falling back to delete + upload approach:", error);
      
      // Fallback to delete + upload approach if replace endpoint doesn't work
      try {
        await apiService.delete(
          getEndpoint(API_ENDPOINTS.TILES.PDFS_DELETE, productId, pdfId)
        );
        
        // Add a small delay to ensure the delete operation completes
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Verify the PDF was deleted by checking the PDF list
        try {
          const pdfsAfterDelete = await this.getPdfs(productId);
          if (pdfsAfterDelete && pdfsAfterDelete.length > 0) {
            console.warn(`PDF still exists after delete, waiting longer...`);
            await new Promise(resolve => setTimeout(resolve, 2000));
          }
        } catch (verifyError) {
          console.warn("Could not verify PDF deletion:", verifyError);
        }
        
        // Upload the new PDF
        const result = await apiService.upload(
          getEndpoint(API_ENDPOINTS.TILES.PDFS_UPLOAD, productId),
          pdfData
        );
        
        return result;
      } catch (fallbackError) {
        console.error("Both replace methods failed:", fallbackError);
        throw fallbackError;
      }
    }
  },

  // Link methods - using proper API endpoints
  addUpdateLink: async (productId, linkData) => {
    const response = await apiService.patch(
      getEndpoint(API_ENDPOINTS.TILES.LINKS_ADD_UPDATE, productId),
      linkData
    );
    return response;
  },

  deleteLink: async (productId) => {
    const response = await apiService.patch(
      getEndpoint(API_ENDPOINTS.TILES.LINKS_DELETE, productId),
      {}
    );
    
    return response;
  },

  getSizes: async (params = {}) => {
    return await apiService.get(API_ENDPOINTS.TILES.SIZES_LIST, { params });
  },

  createSize: async (sizeData) => {
    return await apiService.post(API_ENDPOINTS.TILES.SIZES_CREATE, sizeData);
  },

  updateSize: async (sizeId, sizeData) => {
    return await apiService.put(
      getEndpoint(API_ENDPOINTS.TILES.SIZES_UPDATE, sizeId),
      sizeData
    );
  },

  deleteSize: async (sizeId) => {
    return await apiService.delete(
      getEndpoint(API_ENDPOINTS.TILES.SIZES_DELETE, sizeId)
    );
  },

  undoDeprecationSize: async (sizeId) => {
    return await apiService.post(
      getEndpoint(API_ENDPOINTS.TILES.SIZES_UNDO_DEPRECATION, sizeId)
    );
  },

  getTypes: async (params = {}) => {
    return await apiService.get(API_ENDPOINTS.TILES.TYPES_LIST, { params });
  },

  createType: async (typeData) => {
    return await apiService.post(API_ENDPOINTS.TILES.TYPES_CREATE, typeData);
  },

  updateType: async (typeId, typeData) => {
    return await apiService.put(
      getEndpoint(API_ENDPOINTS.TILES.TYPES_UPDATE, typeId),
      typeData
    );
  },

  deleteType: async (typeId) => {
    return await apiService.delete(
      getEndpoint(API_ENDPOINTS.TILES.TYPES_DELETE, typeId)
    );
  },

  undoDeprecationType: async (typeId) => {
    return await apiService.post(
      getEndpoint(API_ENDPOINTS.TILES.TYPES_UNDO_DEPRECATION, typeId)
    );
  },
};

export const socialService = {
  getPosts: async (params = {}) => {
    return await apiService.get(API_ENDPOINTS.SOCIAL.POSTS_LIST, { params });
  },

  createPost: async (postData) => {
    return await apiService.post(API_ENDPOINTS.SOCIAL.POSTS_CREATE, postData);
  },

  getPostDetail: async (postId) => {
    return await apiService.get(
      getEndpoint(API_ENDPOINTS.SOCIAL.POSTS_DETAIL, postId)
    );
  },

  updatePost: async (postId, postData) => {
    return await apiService.patch(
      getEndpoint(API_ENDPOINTS.SOCIAL.POSTS_UPDATE, postId),
      postData
    );
  },

  deletePost: async (postId) => {
    return await apiService.delete(
      getEndpoint(API_ENDPOINTS.SOCIAL.POSTS_DELETE, postId)
    );
  },

  getReviews: async (sellerId, params = {}) => {
    return await apiService.get(
      getEndpoint(API_ENDPOINTS.SOCIAL.REVIEWS_LIST, sellerId),
      { params }
    );
  },

  createReview: async (sellerId, reviewData) => {
    return await apiService.post(
      getEndpoint(API_ENDPOINTS.SOCIAL.REVIEWS_CREATE, sellerId),
      reviewData
    );
  },

  deleteReview: async (reviewId) => {
    return await apiService.delete(
      getEndpoint(API_ENDPOINTS.SOCIAL.REVIEWS_DELETE, reviewId)
    );
  },

  followSeller: async (sellerId, followData) => {
    return await apiService.post(
      getEndpoint(API_ENDPOINTS.SOCIAL.FOLLOWERS_FOLLOW, sellerId),
      followData
    );
  },

  unfollowSeller: async (sellerId) => {
    return await apiService.delete(
      getEndpoint(API_ENDPOINTS.SOCIAL.FOLLOWERS_UNFOLLOW, sellerId)
    );
  },
};

export const subscriptionService = {
  getList: async (params = {}) => {
    return await apiService.get(API_ENDPOINTS.SUBSCRIPTIONS.LIST, { params });
  },

  getPlans: async (params = {}) => {
    return await apiService.get(API_ENDPOINTS.SUBSCRIPTIONS.PLANS_LIST, {
      params,
    });
  },

  getPlanDetail: async (planId) => {
    return await apiService.get(
      getEndpoint(API_ENDPOINTS.SUBSCRIPTIONS.PLANS_DETAIL, planId)
    );
  },

  createPlan: async (planData) => {
    return await apiService.post(
      API_ENDPOINTS.SUBSCRIPTIONS.PLANS_CREATE,
      planData
    );
  },

  updatePlan: async (planId, planData) => {
    return await apiService.put(
      getEndpoint(API_ENDPOINTS.SUBSCRIPTIONS.PLANS_UPDATE, planId),
      planData
    );
  },

  deletePlan: async (planId) => {
    return await apiService.delete(
      getEndpoint(API_ENDPOINTS.SUBSCRIPTIONS.PLANS_DELETE, planId)
    );
  },

  getBuyerSubscriptions: async (params = {}) => {
    return await apiService.get(API_ENDPOINTS.SUBSCRIPTIONS.BUYERS_LIST, {
      params,
    });
  },

  getBuyerSubscriptionDetail: async (buyerId) => {
    return await apiService.get(
      getEndpoint(API_ENDPOINTS.SUBSCRIPTIONS.BUYERS_DETAIL, buyerId)
    );
  },

  getSellerSubscriptions: async (params = {}) => {
    return await apiService.get(API_ENDPOINTS.SUBSCRIPTIONS.SELLERS_LIST, {
      params,
    });
  },

  getSellerSubscriptionDetail: async (sellerId) => {
    return await apiService.get(
      getEndpoint(API_ENDPOINTS.SUBSCRIPTIONS.SELLERS_DETAIL, sellerId)
    );
  },

  initiatePayment: async (paymentData) => {
    return await apiService.post(
      API_ENDPOINTS.SUBSCRIPTIONS.PAYMENT_INITIATE,
      paymentData
    );
  },

  verifyPayment: async (verificationData) => {
    return await apiService.post(
      API_ENDPOINTS.SUBSCRIPTIONS.PAYMENT_VERIFY,
      verificationData
    );
  },

  getCurrentSellerSubscription: async () => {
    return await apiService.get(
      API_ENDPOINTS.SUBSCRIPTIONS.SELLER_SUBSCRIPTION
    );
  },
};

export const enhancedServices = {
  account: accountService,
  buyer: buyerService,
  seller: sellerService,
  sanitaryware: sanitarywareService,
  tile: tileService,
  social: socialService,
  subscription: subscriptionService,
};

export default enhancedServices;
  